import type { ReactNode } from 'react'
import { INK, W, sparkle } from './shared'

/**
 * Arms and legs: how a Sprout Buddy stands (Sam, 8 and 9 Oct 2026).
 *
 * The first four are the original set. The rest are poses children know
 * from games and pictures: the fighting-game victory poses (Ryu's fist in
 * the air, Chun-Li's jump, Ken's V sign), a thumbs up, Bruce Lee's palm-up
 * "come on", the "I want you!" poster, a superhero with a cape, a dab and
 * heart hands. Then (Sam, 11 Oct) casting a spell and a power stance, to go
 * with the Portal and Fire skins.
 *
 * **Everything cute** (Sam, 9 Oct: the first V sign was "creepy", the thumbs
 * up "looks like a middle finger"). Hands are round mittens. Fingers are
 * short, fat beans, never thin lines, and a thumb comes off the corner of a
 * fist, never its middle.
 *
 * One-arm poses use the buddy's left arm (the viewer's left), so the other
 * hand can still hold the watering can or book. Two-arm poses have no free
 * hand, so nothing is held (`twoHands`).
 */
export const LIMBS = [
  'none', 'simple', 'wave', 'gardener',
  'fist', 'jump', 'vsign', 'thumbs',
  'comeon', 'iwant',
  'hero', 'dab', 'heart', 'power', 'spell',
] as const
export type Limbs = typeof LIMBS[number]

export const LIMB_GROUPS: [string, Limbs[]][] = [
  ['Everyday', ['none', 'simple', 'wave', 'gardener']],
  ['Winning', ['fist', 'jump', 'vsign', 'thumbs']],
  ['Calling you', ['comeon', 'iwant']],
  ['Showing off', ['hero', 'dab', 'heart', 'power', 'spell']],
]

export const LIMB_LABELS: Record<Limbs, string> = {
  none: 'None', simple: 'Arms and legs', wave: 'Waving', gardener: 'Gardener',
  fist: 'Victory fist', jump: 'Victory jump', vsign: 'V sign', thumbs: 'Thumbs up',
  comeon: 'Come on!', iwant: 'I want you!',
  hero: 'Superhero', dab: 'Dab', heart: 'Heart hands',
  power: 'Power stance', spell: 'Casting a spell',
}

type LegStance = 'stand' | 'apart' | 'tuck'
interface Pose { twoHands?: boolean; legs?: LegStance; lift?: boolean }
export const POSES: Record<Limbs, Pose> = {
  none: {}, simple: {}, wave: {}, gardener: {},
  fist: {}, vsign: {}, thumbs: {}, iwant: {}, spell: {},
  jump: { twoHands: true, legs: 'tuck', lift: true },
  comeon: { twoHands: true, legs: 'apart' },
  hero: { twoHands: true, legs: 'apart' },
  dab: { twoHands: true },
  heart: { twoHands: true },
  power: { twoHands: true, legs: 'apart' },
}

export const holdsSomething = (limbs: Limbs) => limbs !== 'none' && !POSES[limbs].twoHands

/**
 * The nearest pose with a hand free, for when a child picks something to
 * hold while the buddy's arms are busy (Sam, 9 Oct: in heart hands every
 * choice in the Holding tab looked the same, because nothing could be held).
 */
export const FREE_HAND: Partial<Record<Limbs, Limbs>> = {
  jump: 'fist', comeon: 'simple', hero: 'simple', dab: 'simple', heart: 'simple', power: 'fist',
}
export const armsFor = (limbs: Limbs, held: string): Limbs =>
  held === 'none' || limbs === 'none' || holdsSomething(limbs) ? limbs : FREE_HAND[limbs] ?? 'simple'

// ---------------------------------------------------------------- pieces

const arm = (d: string, fill: string, k?: string) => (
  <g key={k}>
    <path d={d} stroke={INK} strokeWidth={8} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d={d} stroke={fill} strokeWidth={4} fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </g>
)

const fist = (x: number, y: number, fill: string, k?: string) => (
  <g key={k}>
    <circle cx={x} cy={y} r={5.5} fill={fill} stroke={INK} strokeWidth={2.4} />
    <path d={`M${x - 3} ${y - 1} Q${x} ${y - 3.2} ${x + 3} ${y - 1}`} stroke={INK} strokeWidth={1.2} fill="none" opacity={0.6} />
  </g>
)

/** A short, fat, rounded finger or thumb: a bean, never a line. */
const bean = (cx: number, cy: number, rx: number, ry: number, angle: number, fill: string, k?: string) => (
  <ellipse key={k} cx={cx} cy={cy} rx={rx} ry={ry} transform={`rotate(${angle} ${cx} ${cy})`}
    fill={fill} stroke={INK} strokeWidth={2.2} />
)

/** The right arm when the left one is busy: holding the item out, or resting on the tummy. */
function restingRight(fill: string, held: string) {
  return held !== 'none'
    ? arm('M92 100 C98 104 100 108 99 112', fill)
    : arm('M90 96 C96 108 86 118 78 114', fill)
}

// ---------------------------------------------------------------- behind

/** Anything a pose puts behind the body: the superhero's cape, the power aura. */
export function PoseBehind({ limbs }: { limbs: Limbs }) {
  if (limbs === 'power') {
    // Powering up: a glow in the body's own shape, pointed at the top like a flame.
    return (
      <g className="ws-buddy-pulse">
        <path d="M60 6 C66 20 72 22 78 18 C78 30 84 38 94 40 C90 50 104 66 110 74 C116 96 114 126 100 140 C88 152 72 154 60 154 C48 154 32 152 20 140 C6 126 4 96 10 74 C16 66 30 50 26 40 C36 38 42 30 42 18 C48 22 54 20 60 6 Z"
          fill="#FFE082" opacity={0.55} stroke="#FFC107" strokeWidth={2} strokeLinejoin="round" />
      </g>
    )
  }
  if (limbs !== 'hero') return null
  return (
    <g>
      <path d="M40 66 C24 96 16 124 8 150 C30 144 44 152 60 146 C76 152 90 144 112 150 C104 124 96 96 80 66 Z"
        fill="#E53935" stroke={INK} strokeWidth={W} strokeLinejoin="round" />
      <path d="M30 104 C26 120 22 134 20 146 M90 104 C94 120 98 134 100 146" stroke="#B71C1C" strokeWidth={2} fill="none" strokeLinecap="round" />
    </g>
  )
}

// ---------------------------------------------------------------- arms

export function Arms({ limbs, fill, held }: { limbs: Limbs; fill: string; held: string }) {
  if (limbs === 'none') return null
  const right = restingRight(fill, held)
  switch (limbs) {
    case 'simple':
    case 'gardener':
    case 'wave': {
      const glove = limbs === 'gardener' ? '#7BC043' : fill
      // Waving is an OPEN hand held out to the side, fingers spread, with two
      // little motion lines. Until 18 Oct 2026 it was a round hand raised up
      // and left, and Sam could not tell it from the victory fist.
      const left = limbs === 'wave' ? (
        <g className="ws-buddy-wave">
          {arm('M30 96 C20 94 13 88 11 80', fill)}
          {bean(4.6, 66.5, 2.3, 4.6, -32, fill, 'w1')}{bean(8.6, 63.2, 2.3, 4.8, -12, fill, 'w2')}
          {bean(13.2, 63, 2.3, 4.8, 10, fill, 'w3')}{bean(17.2, 65.6, 2.2, 4.4, 30, fill, 'w4')}
          {bean(18.4, 74, 2.2, 4.2, 70, fill, 'w5')}
          <circle cx={11.5} cy={73} r={6.6} fill={fill} stroke={INK} strokeWidth={2.4} />
          <path d="M23 58 Q27 63 25 69 M27.5 55 Q32.5 62 30 70" stroke={INK} strokeWidth={1.6}
            fill="none" strokeLinecap="round" opacity={0.55} />
        </g>
      ) : (
        <g>
          {arm('M30 98 C24 104 22 112 24 118', fill)}
          {limbs === 'gardener' ? <circle cx={24} cy={119} r={5} fill={glove} stroke={INK} strokeWidth={2.4} /> : null}
        </g>
      )
      return (
        <g>
          {left}
          {right}
          {limbs === 'gardener' ? (
            <circle cx={held !== 'none' ? 99 : 78} cy={held !== 'none' ? 113 : 114} r={5} fill={glove} stroke={INK} strokeWidth={2.4} />
          ) : null}
        </g>
      )
    }
    case 'fist': // Ryu: one fist punched straight up, high over the head, with a burst
      return (
        <g>
          <g className="ws-buddy-pump">
            {arm('M30 95 C24 80 18 66 15 54', fill)}{fist(14, 49, fill)}
            {sparkle(4, 38, 4.5, 'f1', '#FFD54F')}{sparkle(25, 40, 3.4, 'f2', '#FFD54F')}
          </g>
          {right}
        </g>
      )
    case 'vsign': // Ken's V, made cute: two short round fingers on a mitten
      return (
        <g>
          {arm('M30 98 C20 94 16 84 19 75', fill)}
          {bean(15.2, 61.5, 3, 5.4, -20, fill, 'a')}{bean(23.8, 61.5, 3, 5.4, 20, fill, 'b')}
          <circle cx={19.5} cy={69.5} r={6.4} fill={fill} stroke={INK} strokeWidth={2.4} />
          <path d="M16 71 Q19.5 73 23 71" stroke={INK} strokeWidth={1.1} fill="none" opacity={0.45} />
          {sparkle(33, 54, 4.5, 'v', '#FFD54F')}
          {right}
        </g>
      )
    case 'thumbs': // the thumb comes off the fist's corner, never its middle
      return (
        <g>
          {arm('M30 98 C24 104 17 101 15 94', fill)}
          {bean(20.5, 75.5, 3.3, 5.6, 14, fill, 't')}
          <rect x={7.5} y={79} width={15} height={13} rx={5.5} fill={fill} stroke={INK} strokeWidth={2.4} />
          <path d="M10 83.5 L17 83.5 M10 87.5 L17 87.5" stroke={INK} strokeWidth={1.2} strokeLinecap="round" opacity={0.5} />
          {right}
        </g>
      )
    case 'jump': // Chun-Li: up in the air, both fists high
      return (
        <g>
          {arm('M30 94 C22 80 16 68 12 58', fill)}{fist(11, 53, fill)}
          {arm('M90 94 C98 80 104 68 108 58', fill)}{fist(109, 53, fill)}
        </g>
      )
    case 'comeon': // Bruce Lee's "come on": a palm-up mitten whose fingertips curl in together
      return (
        <g>
          {arm('M30 100 C20 102 12 100 5 97', fill)}
          <ellipse cx={1} cy={95.5} rx={7.5} ry={4.2} fill={fill} stroke={INK} strokeWidth={2.4} />
          <g className="ws-buddy-beckon">{bean(-6, 91, 3.6, 5, -28, fill, 'c')}</g>
          <g stroke={INK} strokeWidth={1.6} fill="none" strokeLinecap="round" opacity={0.55}>
            <path d="M-12 83 Q-15 87 -12 91" /><path d="M-16 81 Q-20 87 -16 93" />
          </g>
          {arm('M90 98 C99 94 99 84 92 80', fill)}{fist(90, 77, fill)}
        </g>
      )
    case 'iwant': // the poster: a fat finger pointing out at you, and it says so
      return (
        <g>
          {arm('M30 98 C24 108 28 116 36 119', fill)}
          <ellipse cx={44} cy={121} rx={9} ry={8} fill={fill} stroke={INK} strokeWidth={2.4} />
          <path d="M38 124 Q44 127 50 124" stroke={INK} strokeWidth={1.1} fill="none" opacity={0.45} />
          {/* the finger, coming towards you: short and fat, with its nail showing */}
          <path d="M42.5 118 L44 110.5 A5 5 0 0 1 53.5 112.5 L52 120 Z" fill={fill} stroke={INK} strokeWidth={2.4} strokeLinejoin="round" />
          <ellipse cx={49} cy={110.8} rx={3} ry={2.3} transform="rotate(12 49 110.8)" fill="#FFF3EA" stroke={INK} strokeWidth={1} />
          {/* a speech bubble, because a finger pointing at you cannot be drawn flat */}
          <g>
            <path d="M98 42 H126 Q132 42 132 48 V58 Q132 64 126 64 H106 L96 72 L100 64 H98 Q92 64 92 58 V48 Q92 42 98 42 Z"
              fill="#FFFFFF" stroke={INK} strokeWidth={2.2} strokeLinejoin="round" />
            <text x={112} y={58} textAnchor="middle" fontSize={12} fontWeight={800} fill={INK}
              fontFamily="'Fredoka', 'Nunito', sans-serif">YOU!</text>
          </g>
          {right}
        </g>
      )
    case 'hero': { // hands on hips, elbows out
      const one = <g>{arm('M30 96 C16 98 12 110 26 118', fill)}{fist(27, 118, fill)}</g>
      return <g>{one}<g transform="translate(120 0) scale(-1 1)">{one}</g></g>
    }
    case 'dab':
      return (
        <g>
          {arm('M90 96 L113 68', fill)}{fist(115, 65, fill)}
          {arm('M30 96 C28 82 40 72 62 74', fill)}{fist(65, 74, fill)}
        </g>
      )
    case 'heart':
      return (
        <g>
          {arm('M30 98 C34 112 42 120 50 121', fill)}
          {arm('M90 98 C86 112 78 120 70 121', fill)}
          <g className="ws-buddy-beat">
            <path d="M60 131 C49 124 47 113 53 112 C56 111.5 59 114 60 117 C61 114 64 111.5 67 112 C73 113 71 124 60 131 Z"
              fill="#FF5C8A" stroke={INK} strokeWidth={2.2} strokeLinejoin="round" />
          </g>
          <circle cx={51} cy={120} r={4.5} fill={fill} stroke={INK} strokeWidth={2.2} />
          <circle cx={69} cy={120} r={4.5} fill={fill} stroke={INK} strokeWidth={2.2} />
        </g>
      )
    case 'power': { // powering up: fists pulled in, feet wide, energy crackling off the sides
      const one = <g>{arm('M30 98 C20 106 28 114 40 111', fill)}{fist(43, 110, fill)}</g>
      return (
        <g>
          {one}<g transform="translate(120 0) scale(-1 1)">{one}</g>
          <g stroke="#FFB300" strokeWidth={2.6} fill="none" strokeLinecap="round" strokeLinejoin="round">
            {['M4 92 L-2 86 L3 84 L-3 76', 'M116 92 L122 86 L117 84 L123 76',
              'M8 124 L1 122 L5 118 L-2 114', 'M112 124 L119 122 L115 118 L122 114'].map((d, i) => (
              <path key={i} d={d} className="ws-buddy-twinkle" style={{ animationDelay: `${i * 0.25}s` }} />
            ))}
          </g>
        </g>
      )
    }
    case 'spell': // one hand up, fingers spread, magic swirling out of it; the other hand is free for a spell book
      return (
        <g>
          {arm('M30 96 C20 90 12 82 9 74', fill)}
          {bean(2.5, 63.5, 2.6, 4.6, -35, fill, 'a')}{bean(8.5, 61.5, 2.6, 4.8, 0, fill, 'b')}{bean(14.5, 63.5, 2.6, 4.6, 35, fill, 'c')}
          <circle cx={8.5} cy={70} r={6.4} fill={fill} stroke={INK} strokeWidth={2.4} />
          <path className="ws-buddy-orbit" d="M9 54 C-1 54 -4 42 5 39 C13 36 17 46 11 48 C7 49 6 45 8 44"
            stroke="#B388FF" strokeWidth={2.4} fill="none" strokeLinecap="round" strokeDasharray="5 4" />
          {sparkle(-3, 48, 5, 'm1', '#FFD54F')}{sparkle(21, 44, 4, 'm2', '#4DD0E1')}
          {sparkle(10, 30, 3.5, 'm3', '#B388FF')}{sparkle(24, 58, 2.5, 'm4', '#FFD54F')}
          {right}
        </g>
      )
  }
  return null
}

// ---------------------------------------------------------------- legs

export function Legs({ limbs, fill }: { limbs: Limbs; fill: string }) {
  if (limbs === 'none') return null
  const boot = limbs === 'gardener' ? '#5BAA3C' : null
  const stance = POSES[limbs].legs ?? 'stand'
  // Turned at the hip: a little for a wide stance, a lot for a jump's tucked kick.
  const turn = stance === 'tuck' ? 40 : stance === 'apart' ? 12 : 0
  return (
    <g>
      {[48, 72].map((x) => (
        <g key={x} transform={turn ? `rotate(${x < 60 ? turn : -turn} ${x} 134)` : undefined}>
          <path d={`M${x - 5} 134 L${x - 5} 146 Q${x - 5} 150 ${x} 150 Q${x + 5} 150 ${x + 5} 146 L${x + 5} 134`}
            fill={fill} stroke={INK} strokeWidth={W} strokeLinejoin="round" />
          {boot ? (
            <path d={`M${x - 6.5} 141 L${x + 6.5} 141 L${x + 7.5} 150 Q${x} 152 ${x - 7.5} 150 Z`}
              fill={boot} stroke={INK} strokeWidth={2.4} strokeLinejoin="round" />
          ) : null}
        </g>
      ))}
    </g>
  )
}

/** Under a jumping buddy: its shadow on the ground, and lines for the hop. */
export function JumpGround(): ReactNode {
  return (
    <g>
      <ellipse cx={60} cy={156} rx={20} ry={3} fill={INK} opacity={0.15} />
      <g stroke={INK} strokeWidth={2} strokeLinecap="round" opacity={0.3}>
        <path d="M44 146 L42 152" /><path d="M60 148 L60 154" /><path d="M76 146 L78 152" />
      </g>
    </g>
  )
}
